import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Modal,
  Row,
  Table,
} from "react-bootstrap";
import Loader from "react-loader-spinner";
import moment from "moment";
import Axios from "../utils/axios";
import { Helper } from "../helper";
import CustomStyle from "../styles";
import { AuthContext } from "../context/auth";
import { showToast } from "../utils/general";

const money = (amount) =>
  `₦${Number(amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function PendingCheckouts() {
  const context = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [retryingId, setRetryingId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const fetchData = async (nextPage = 1) => {
    const { token } = context.authState;
    setLoading(true);
    try {
      const response = await Helper.getData(
        `${process.env.REACT_APP_API_V2}/admin/orders/checkouts?status=pending&page=${nextPage}&limit=20`,
        token,
      );
      const payload = response?.data;
      setRows(Array.isArray(payload?.results) ? payload.results : []);
      setTotalPages(payload?.totalPages || 0);
      setPage(payload?.currentPage || nextPage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1);
  }, []);

  const retryCheckout = async () => {
    if (!selected) return;
    const { token } = context.authState;
    setRetryingId(selected.id);
    try {
      await Axios.post(
        `${process.env.REACT_APP_API_V2}/admin/orders/checkouts/${selected.id}/retry`,
        {},
        { headers: { "x-access-token": token } },
      );
      showToast("Order placed", "success");
      setSelected(null);
      fetchData(page);
    } catch (error) {
      showToast(error?.response?.data?.message || "Could not place this order");
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <>
      <Modal
        show={selected !== null}
        onHide={() => setSelected(null)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Checkout {selected?.reference}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selected && (
            <>
              <p className="mb-1">
                <b>Customer:</b> {selected.customerName || "—"} (
                {selected.customerEmail})
              </p>
              <p className="mb-1">
                <b>Phone:</b> {selected.phone || "—"}
              </p>
              <p className="mb-1">
                <b>Payment:</b> {selected.paymentOption}
              </p>
              <p className="mb-1">
                <b>Fulfilment:</b> {selected.deliveryOption}
                {selected.warehouseName ? ` · ${selected.warehouseName}` : ""}
              </p>
              {selected.warehouseAddress ? (
                <p className="mb-1">
                  <b>Store:</b> {selected.warehouseAddress}
                </p>
              ) : null}
              {selected.deliveryAddress ? (
                <p className="mb-3">
                  <b>Delivery address:</b> {selected.deliveryAddress}
                </p>
              ) : null}

              <Table size="sm" responsive>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Qty</th>
                    <th>Price</th>
                    <th>Line total</th>
                  </tr>
                </thead>
                <tbody>
                  {(selected.cart || []).map((item, index) => (
                    <tr key={`${item.name}-${index}`}>
                      <td>{item.name || "—"}</td>
                      <td>{item.quantity}</td>
                      <td>{money(item.price)}</td>
                      <td>
                        {money(Number(item.price) * Number(item.quantity))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <div className="mt-3">
                <p className="mb-1">
                  <b>Subtotal:</b> {money(selected.discounts?.subTotal)}
                </p>
                <p className="mb-1">
                  <b>Coupon:</b> {money(selected.discounts?.couponAmount)}
                </p>
                <p className="mb-1">
                  <b>Promo:</b> {money(selected.discounts?.promoAmount)}
                </p>
                <p className="mb-1">
                  <b>Delivery fee:</b>{" "}
                  {money(selected.discounts?.deliveryAmount)}
                </p>
                <p className="mb-1">
                  <b>Shopping bags:</b> {selected.noOfShoppingBag || 0} ·{" "}
                  {money(selected.discounts?.shoppingBagFee)}
                </p>
                <p className="mb-0">
                  <b>Total:</b> {money(selected.discounts?.totalPayableAmount)}
                </p>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelected(null)}>
            Close
          </Button>
          <Button
            variant="primary"
            disabled={Boolean(retryingId)}
            onClick={retryCheckout}
            style={{
              background: CustomStyle.colors.primary,
              borderColor: CustomStyle.colors.primary,
              color: CustomStyle.colors.white,
            }}
          >
            {retryingId ? "Placing..." : "Place order"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Container fluid>
        <Row>
          <Col md="12">
            <Card className="card-tasks">
              <Card.Header>
                <Card.Title as="h4">Pending checkouts</Card.Title>
                <p className="card-category">
                  Payments that were started but never became an order
                </p>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div style={{ textAlign: "center", padding: "20px 0" }}>
                    <Loader
                      type="Oval"
                      color={CustomStyle.colors.primary}
                      height={50}
                      width={50}
                    />
                  </div>
                ) : rows.length === 0 ? (
                  <p>No pending checkouts</p>
                ) : (
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Customer</th>
                        <th>Reference</th>
                        <th>Method</th>
                        <th>Amount</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.id}>
                          <td>
                            {moment(row.createdAt).format("DD MMM YYYY, HH:mm")}
                          </td>
                          <td>
                            <div>{row.customerName || "—"}</div>
                            <small>{row.customerEmail}</small>
                          </td>
                          <td>{row.reference}</td>
                          <td>{row.paymentOption}</td>
                          <td>{money(row.totalAmount)}</td>
                          <td>
                            <Button
                              size="sm"
                              variant="light"
                              onClick={() => setSelected(row)}
                            >
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-end gap-2">
                    <Button
                      variant="light"
                      disabled={page <= 1 || loading}
                      onClick={() => fetchData(page - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="light"
                      disabled={page >= totalPages || loading}
                      onClick={() => fetchData(page + 1)}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default PendingCheckouts;
